// class OptionGroup{
// 	public options:{} = {};
// 	public groups:{} = {};
// }

import {PropertyOption} from './PropertyOption';

import * as THREE from "three";
import * as _ from "lodash";


export enum PropertyType {
	Choice = 'choice',
	Number = 'number',
	Color = 'color',
}

enum PositionHandle {
	X = 0,
	Y = 1,
	Z = 2
}

namespace PositionHandle{
	/**
	 * Returns uniform .value setter callback - https://threejs.org/docs/#api/math/Vector3
	 * @param axis
	 * @return {string}
	 */
	export function getUniformCallback(axis):string{
		switch (axis){
			case PositionHandle.X: return 'setX';
			case PositionHandle.Y: return 'setY';
			case PositionHandle.Z: return 'setZ';
		}
		throw new Error(`Invalid axis: ${axis}`);
	}
}

class PositionProperty{
	/** Selector (used as index) */
	public selector: string;
	/** Name of property controller (bone) */
	public name: string;
	/** Direction letter (PositionHandle) */
	public dimension: PositionHandle;
	/** Formula which determines final value (uses "@" as value input) */
	public value: string;

	constructor(data){
		this.selector = data.rule;
		let dimension: string;
		[this.name, dimension] = this.selector.split(':');
		this.dimension = PositionHandle[dimension.toUpperCase()];
		this.value = data.value;
	}
}


class ColorProperty{
	/** Selector (used as index) */
	public selector: string;
	/** Formula which determines final value (uses "@" as value input) */
	public value: string;

	/** Material name to change */
	public material: string;
	/** Uniform name to change */
	public uniform: string;

	constructor(data){
		this.selector = data.rule;
		let objectClass: string;
		[objectClass, this.material, this.uniform] = this.selector.split(':');
		this.value = data.value;

		if (objectClass != 'material')
			throw new Error(`Color property does not support ${objectClass} for now!`);
	}
}

export type UniformSetter = (uniform: THREE.Uniform, value: number) => void;

export class Property{
	public options:{} = {};
	public groups:{} = {};
	public defaultValue:string = null;

	public set_models: { [id:string]:string } = {};
	public set_materials: { [id:string]:string } = {};
	public set_uniforms: { [id:string]:string } = {};
	public cb_position: { [id:string]:UniformSetter } = {};

	protected validValues:string[] = [];
	public optionsAll: { [id:string]:PropertyOption } = {};

	public type:PropertyType = PropertyType.Choice;

	constructor(id: string, data){
		if (data.hasOwnProperty('default'))
			this.defaultValue = data.default;
		Object.assign(this, data);
		// if (data.hasOwnProperty('set_position')){
		switch (this.type) {
			case PropertyType.Number:
				for (let rule of data.set_position) {
					let def = new PositionProperty(rule);
					this.cb_position[def.selector] = <UniformSetter> new Function(
						'u', 'v',
						`u.value.${PositionHandle.getUniformCallback(def.dimension)}(${def.value.replace(/@/, 'v')});`
					);

					/*
						Example:

						set-position: {
							"size:X": "0.5 + @ * 1.5"
						}

						will lead to calling

						uniform.setX(0.5 + value * 1.5);
					*/
				}
				if (this.defaultValue === null)
					console.warn(`Property [${id}] has no default value`);
				break;
			case PropertyType.Color:
				for (let rule of data.set_uniforms) {
					let def = new ColorProperty(rule);
					this.set_uniforms[def.selector] = def.value;
				}

				if (this.defaultValue === null)
					console.warn(`Property [${id}] has no default value`);
				break;
			default:
				this.validValues = this.loadGroupValues(this);
				if (this.validValues.indexOf(this.defaultValue) < 0)
					this.defaultValue = this.validValues[0];
		}
	}

	getOption(value:string, fallback:string = null):PropertyOption|string{
		return (this.optionsAll.hasOwnProperty(value))
			? this.optionsAll[value]
			: fallback;
	}

/**
 * Validates value and returns valid value
 * @param {string} value proposed value
 * @param {string} product Current product
 * @returns {string} real value - this can be option or group id
 */
	getValidValue(value:string = null, product:string = null):string{
		let valValid:string = (this.validValues.indexOf(value) >= 0)
			? value
			: this.defaultValue;

		// return default, if compatible with current product
		if (this.optionsAll[valValid].match(product))
			return valValid;

		// find and return first option compatible with product
		for (let opt in this.optionsAll)
			if (this.optionsAll[opt].match(product))
				return opt;

		// no value is compatible with product
		return null;
	}

	propertyValues(value:string, propertyItem:{} = null):string[]{
		if (!propertyItem)
			propertyItem = this;
		if (propertyItem.hasOwnProperty('options') && propertyItem['options'].hasOwnProperty(value))
			return [value];	// option id match
		if (propertyItem.hasOwnProperty('groups')){
			if (propertyItem['groups'].hasOwnProperty(value))
				return [value];	// group id match
			for (let group in propertyItem['groups'])
				if (propertyItem['groups'].hasOwnProperty(group)){
					let match = this.propertyValues(value, propertyItem['groups'][group]);
					if (match.length){
						match.push(group);
						return match;
					}
				}
		}
		return [];
	}

	/**
	 * Lists all values of all options and groups
	 * Initializes internal validOptions property
	 */
	protected loadGroupValues(data:{}):string[]{
		let opts:string[] = [];
		if (data.hasOwnProperty('options'))
			for (let opt in data['options'])
				if (data['options'].hasOwnProperty(opt)){
					opts.push(opt);
					this.optionsAll[opt] = new PropertyOption(data['options'][opt]);
				}

		if (data.hasOwnProperty('groups'))
			for (let gID in data['groups'])
				if (data['groups'].hasOwnProperty(gID))
					opts = opts.concat(this.loadGroupValues(data['groups'][gID]));

		return opts;
	}
}

// export class PropertySet extends Map<string,PropertyOption> { // unfortunately, TS can not handle this :(
export class PropertySet {
	protected propertyValues: Map<string,PropertyOption>;
	protected otherProperties: {[property:string]:string|number} = {}; // non-choice properties

	constructor(propertySet:PropertySet)
	constructor(properties:{[property:string]:Property}, configuration:{[property:string]:string|number})
	constructor(properties?:PropertySet|{[property:string]:Property}, configuration?:{[property:string]:string|number}) {
		if (properties instanceof PropertySet) {	// clone constructor has to copy other properties
			this.propertyValues = new Map<string,PropertyOption>(properties.propertyValues);
			this.otherProperties = _.clone(properties.otherProperties);
		}

		if (configuration) {
			this.propertyValues = new Map<string,PropertyOption>();
			if (properties instanceof PropertySet)
				console.warn(`Ivalid parameters for new PropertySet() - should not combine cloning and configuration`);
			else
				_.each(configuration, (value, property) => {
					if (!properties.hasOwnProperty(property)) console.warn('no property');
					if (!properties.hasOwnProperty(property))
						throw new Error(`Invalid property ${property}`);
					if (properties[property].type == PropertyType.Choice) {
						let optValue = properties[property].getOption(<string>value);
						if (optValue instanceof PropertyOption)
							this.propertyValues.set(property, optValue);
						else
							throw new Error(`No valid option for value [${property}]=${value}`);
					}
					else
						this.otherProperties[property] = value;
				});
		}
	}

	/**
	 * Property object back from original
	 */
	getPropertyObject():{[property:string]:string|number}{
		let properties = _.clone(this.otherProperties);
		this.propertyValues.forEach((value, property) => {
			properties[property] = value.realValue;
		});
		return properties;
	}

	forEach(callbackfn: (value: PropertyOption, key: string, map: Map<string, PropertyOption>) => void): void {
		this.propertyValues.forEach(callbackfn)
	}

	get(key: string): PropertyOption | undefined {
		return this.propertyValues.get(key);
	}
	set(key: string, value: PropertyOption): this {
		this.propertyValues.set(key, value);
		return this;
	}
}

/**
 * Product Rule-set
 */
export class RuleSet{
	protected rules:{[prperty:string]:string[]} = {};

	constructor(rules:{[prperty:string]:string|string[]} = {}) {
		_.each(rules, (rule, property) => {
			this.rules[property] = (rule instanceof Array)	// unify rules to string[]
				? rule
				: [<string>rule];
		});
	}

	/**
	 * How much is configuration divergent from current ruleset. 0 means perfect match
	 * @param configuration
	 */
	divergence(configuration: PropertySet):number{
		let divergence = 0;
		configuration.forEach((option:PropertyOption, property:string) => {
			if (this.rules.hasOwnProperty(property))
				// try to find '*', value or any alias of current option in all rules
				if (_.intersection(this.rules[property], option.matchingIDs).length <= 0)
					divergence++; // mismatched property adds divergence
		});
		return divergence;
	}

	/**
	 * Builds property set from rule list, which matches all rules
	 */
	getPropertySet(propeties: {[property:string]:Property}, original: PropertySet): PropertySet{

		let newSet = new PropertySet(original);
		_.each(this.rules, (rules, property) => {
			// let option = original.get(property);
			if (_.intersection(rules, original.get(property).matchingIDs).length <= 0){	// rule does not match current
				let found = false;
				_.each(propeties[property].optionsAll, (propertyData:PropertyOption) => {
					let candidate = new PropertyOption(propertyData);
					if (_.intersection(rules, candidate.matchingIDs).length > 0){	// option matches rule
						newSet.set(property, candidate);
						found = true;
						return false;	// continue to next property
					}
				});
				// did not find any option for rule
				if (!found)
					throw new Error(`No valid option for [${property}] found for rules [${rules.join(', ')}]`);
			}
		});
		return newSet;
	}
}


// WEBPACK FOOTER //
// ./src/script/app3D/data/Property.ts